import { QueryObserverResult, useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useOrganizations } from '../../api/organization-client/organization-client.hooks';
import { usePortfolios } from '../../api/portfolio-client/portfolio-client.hooks';
import { usePortfolioProjects } from '../../api/summary-client/summary-client.hooks';
import { UserContext } from '../../contexts';
import { updateI18nResources } from '../../helpers/i18n-helpers/i18n.helpers';
import { isValidAccessToken } from '../../helpers/storage/storage';
import { useAuthStore } from '../../store/auth-store/auth-store';
import { useGlobalStore } from '../../store/global-store/global-store';
import { initGlobalState, setTeams } from '../../store/global-store/global-store.actions';

/**
 * Initializes the data for the application.
 *
 * @return {void}
 */
const useDataInitializer = (): void => {
  const { user } = useContext(UserContext);
  const initializedGlobalState = useGlobalStore((state) => state.initialized);
  const signUpInitialized = useAuthStore((state) => state.signUpInitialized);
  const organization = useGlobalStore((state) => state.organization);
  const queryClient = useQueryClient();

  // Invalidate organizations query on page load to ensure fresh feature flags
  // This helps SSO users get updated feature flags without manual logout
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['organizations'] });
  }, [queryClient]);

  const isValidAccess = isValidAccessToken();
  const dataFetchingEnabled =
    isValidAccess && !initializedGlobalState && !!user?.terms_and_conditions_accepted && !signUpInitialized;

  const { organizations = [], query: organizationsQuery } = useOrganizations({ enabled: dataFetchingEnabled });
  const { portfolios = [], query: portfoliosQuery } = usePortfolios({ enabled: dataFetchingEnabled });

  // Initialize global state
  useEffect(() => {
    if (organizations.length && portfolios.length && !initializedGlobalState) {
      initGlobalState(organizations, portfolios);
    }
  }, [organizations, portfolios, initializedGlobalState]);

  useEffect(() => {
    if (organization) {
      updateI18nResources(organization);
    }
  }, [organization]);

  const pendoInitialized = useRef(false);

  useEffect(() => {
    if (user && organization) {
      const [firstName = '', ...lastNameParts] = user.name.split(' ');
      const pendoOptions = {
        visitor: {
          id: user.id,
          email: user.email,
          firstName,
          lastName: lastNameParts.join(' '),
        },
        account: {
          id: organization.id,
          accountName: organization.name,
        },
      };

      if (!pendoInitialized.current) {
        if (window.pendo) {
          try {
            window.pendo.initialize(pendoOptions);
            pendoInitialized.current = true;
          } catch (error) {
            console.error('pendo.initialize failed during useDataInitializer', error);
          }
        }
      } else {
        window.pendo?.identify(pendoOptions);
      }

      window.heap?.addUserProperties({
        organization_id: organization.id,
        organization_name: organization.name,
      });

      window.userpilot?.identify(user.id, {
        name: user.name,
        email: user.email,
        company: {
          id: organization.id,
          name: organization.name,
        },
      });
    }
  }, [user, organization]);

  useHandleErrors(organizationsQuery);
  useHandleErrors(portfoliosQuery);

  useTeamsInitializer();
};

/**
 * Initializes the teams for the selected portfolio.
 *
 * @return {Object} An object containing a boolean indicating if the teams are being fetched.
 */
const useTeamsInitializer = (): { isFetching: boolean } => {
  const portfolio = useGlobalStore((state) => state.portfolio);

  const isValidAccess = isValidAccessToken();

  const { projects: teams, query } = usePortfolioProjects(portfolio?.id, {
    enabled: Boolean(portfolio?.id) && isValidAccess,
    staleTime: 1000 * 60 * 5,
  });

  // Switching between two portfolios that both resolve self-heals on its own: the
  // query re-fetches under the new key and the effect below overwrites `teams` once
  // it lands. But when the switch lands on NO portfolio at all (e.g. the newly
  // selected organization has no portfolio in the loaded `portfolios` array), the
  // query above is permanently disabled and will never run again — nothing would
  // ever overwrite the previous portfolio's (and therefore previous organization's)
  // stale projects still sitting in the store. Clear explicitly for that one
  // transition only; clearing on every switch would also wipe `teams` while a
  // sibling consumer (e.g. Work Periods board selection) is still relying on the
  // previous portfolio's data during the brief in-flight window.
  useEffect(() => {
    if (!portfolio?.id) {
      setTeams([]);
    }
  }, [portfolio?.id]);

  // Initialize portfolio teams
  useEffect(() => {
    if (teams) {
      setTeams(teams);
    }
  }, [teams]);

  useHandleErrors(query);

  return { isFetching: query.isFetching };
};

/**
 * Handles errors from a query by redirecting to the out-of-bloom page with the error state.
 *
 * @param {QueryObserverResult<T>} query - The result of the query.
 * @return {void}
 */
const useHandleErrors = <T>(query: QueryObserverResult<T>): void => {
  const navigate = useNavigate();

  useEffect(() => {
    if (query.isError) {
      const error = query.error as Error;
      const errorState = {
        errorMessage: error?.message || 'Unknown error',
        errorStatus: 500,
      };
      navigate(`/application/out-of-bloom`, { state: errorState });
    }
  }, [query, navigate]);
};

export { useDataInitializer, useTeamsInitializer };
